import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';
import Icon from '../components/Icon';
import SuccessOverlay from '../components/SuccessOverlay';
import api from '../services/api';

const MyDriver = () => {
  const { user, refreshUser } = useAuth();

  const seasonDriverObj = user?.seasonDriver
    ? (F1_DRIVERS.find(d => d.code === user.seasonDriver.code || d.name === user.seasonDriver.name) || null)
    : null;

  const [selected, setSelected] = useState(user?.favoriteDriver || null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSelect = (driver) => {
    setSelected(prev => prev?.name === driver.name ? null : driver);
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post('/auth/season-driver', { driver: selected });
      await refreshUser();
      setSuccess(true);
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-white">Mon pilote</h1>
        <p className="text-zinc-400 mt-1">Ton pilote de saison est verrouillé. Ses résultats t'apportent des points dans le classement.</p>
      </div>

      {/* ── Season driver — LOCKED ── */}
      {seasonDriverObj && (
        <div className="card mb-8 overflow-hidden relative"
          style={{ borderColor: seasonDriverObj.color + '55' }}>
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(135deg, ${seasonDriverObj.color}12 0%, transparent 60%)` }} />

          <div className="flex items-center gap-5 relative z-10">
            <div className="w-24 h-24 shrink-0 overflow-hidden rounded-xl"
              style={{ background: seasonDriverObj.color + '20' }}>
              <img
                src={driverImgUrl(seasonDriverObj.img)}
                alt={seasonDriverObj.name}
                className="w-full h-full object-cover object-top"
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: seasonDriverObj.color }}>
                Pilote de saison · {seasonDriverObj.team}
              </p>
              <p className="text-white font-black text-2xl leading-tight">{seasonDriverObj.name}</p>
              <p className="text-zinc-500 text-xs mt-0.5">#{seasonDriverObj.number}</p>
              {(user?.seasonDriverPoints > 0) && (
                <p className="text-sm font-black mt-1" style={{ color: seasonDriverObj.color }}>
                  +{user.seasonDriverPoints} pts de saison
                </p>
              )}
            </div>
            <div className="shrink-0 flex flex-col items-center gap-1 text-zinc-500">
              <div className="w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                <Icon name="lock" size={16} />
              </div>
              <span className="text-[10px] text-zinc-600 font-medium">Verrouillé</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t relative z-10" style={{ borderColor: seasonDriverObj.color + '25' }}>
            <p className="text-xs text-zinc-500 flex items-center gap-1.5">
              <span>ℹ️</span>
              Ce choix est définitif — impossible de changer de pilote de saison.
            </p>
          </div>
        </div>
      )}

      {!seasonDriverObj && (
        <>
          <div className="mb-4">
            <h2 className="text-base font-bold text-white mb-1">Choisis ton pilote de saison</h2>
            <p className="text-zinc-500 text-xs">Ce choix est définitif et ne pourra pas être modifié.</p>
          </div>

          {selected && (
            <div className="card mb-4 flex items-center gap-4 overflow-hidden relative"
              style={{ borderColor: selected.color + '44', background: selected.color + '0A' }}>
              <div className="w-14 h-14 shrink-0 overflow-hidden rounded-xl"
                style={{ background: selected.color + '20' }}>
                <img src={driverImgUrl(selected.img)} alt={selected.name}
                  className="w-full h-full object-cover object-top"
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: selected.color }}>
                  {selected.team} · #{selected.number}
                </p>
                <p className="text-white font-black text-base leading-tight">{selected.name}</p>
              </div>
              <button type="button" onClick={() => { setSelected(null); setSuccess(false); }}
                className="shrink-0 text-zinc-500 hover:text-zinc-300 pr-2">
                <Icon name="x" size={16} />
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {F1_DRIVERS.map(driver => {
              const isSelected = selected?.name === driver.name;
              return (
                <button key={driver.name} type="button" onClick={() => handleSelect(driver)}
                  className="relative flex flex-col items-center rounded-xl border-2 overflow-hidden transition-all duration-150 text-center cursor-pointer"
                  style={{
                    borderColor: isSelected ? driver.color : 'rgb(39,39,42)',
                    background: isSelected ? driver.color + '14' : 'rgb(24,24,27)',
                    transform: isSelected ? 'scale(1.03)' : undefined,
                  }}>
                  <div className="w-full h-28 overflow-hidden relative"
                    style={{ background: `linear-gradient(to bottom, ${driver.color}22, ${driver.color}08)` }}>
                    <img src={driverImgUrl(driver.img)} alt={driver.name}
                      className="w-full h-full object-cover object-top"
                      onError={e => { e.target.style.display = 'none'; }} />
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center shadow"
                        style={{ background: driver.color }}>
                        <Icon name="check" size={11} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="px-2 py-2 w-full" style={{ borderTop: `1px solid ${driver.color}22` }}>
                    <p className="text-white text-xs font-black leading-tight truncate">{driver.name}</p>
                    <p className="text-[10px] font-semibold mt-0.5 truncate" style={{ color: driver.color }}>
                      #{driver.number} · {driver.team}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          <SuccessOverlay visible={success} message="Pilote enregistré !"
            color={selected?.color || '#22c55e'} onDone={() => setSuccess(false)} />

          <button type="button" onClick={handleSave} disabled={saving}
            className="btn-primary w-full py-3 text-base">
            {saving ? 'Enregistrement...' : 'Sauvegarder'}
          </button>
        </>
      )}
    </div>
  );
};

export default MyDriver;
